import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from './ui/button';
import { Ship, LogOut, User, Menu, Home, LayoutDashboard, X, FileText, TrendingUp, Users, Megaphone, BarChart3, Newspaper, ClipboardList, Gauge, MessageSquare, Sun, Moon } from 'lucide-react';
import api from '../utils/api';

const Header = () => {
  const { user, logout, isAdmin } = useAuth();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  
  useEffect(() => {
    if (user && !isAdmin) {
      loadUnreadCount();
    }
  }, [user, isAdmin]);

  const loadUnreadCount = async () => {
    try {
      const response = await api.get('/messages/unread-count');
      setUnreadMessages(response.data?.count || 0);
    } catch (error) {
      // Silently fail - messages might not be available
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    setMobileMenuOpen(false);
  };

  // Home goes to /home for regular users, /admin for admins
  const homeDestination = isAdmin ? '/admin' : '/home';

  const handleNavClick = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  return (
    <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-14 sm:h-16">
          {/* Logo - clickable to appropriate dashboard */}
          <Link to={homeDestination} className="flex items-center gap-1.5 sm:gap-2 hover:opacity-80 transition-opacity flex-shrink-0" data-testid="header-logo">
            <img 
              src="/logo-icon.png" 
              alt="CTC Logo" 
              className="h-8 w-8 sm:h-10 sm:w-10 object-contain rounded-lg"
            />
            <span className="text-base sm:text-xl font-outfit font-bold">
              <span className="text-cyan-500">CTC</span>
              <span className="hidden sm:inline text-slate-700"> Cargo</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6">
            <Link to={homeDestination} className="text-sm font-medium text-slate-600 hover:text-cyan-500 transition-colors flex items-center gap-1" data-testid="nav-home">
              {isAdmin ? <LayoutDashboard className="h-4 w-4" /> : <Home className="h-4 w-4" />}
              {isAdmin ? 'Admin Home' : 'Home'}
            </Link>
            {!isAdmin && (
              <Link to="/dashboard" className="text-sm font-medium text-slate-600 hover:text-cyan-500 transition-colors flex items-center gap-1">
                <Gauge className="h-4 w-4" />
                Dashboard
              </Link>
            )}
            <Link to="/rfqs" className="text-sm font-medium text-slate-600 hover:text-cyan-500 transition-colors">
              RFQs
            </Link>
            <Link to="/quotes" className="text-sm font-medium text-slate-600 hover:text-cyan-500 transition-colors">
              Quotes
            </Link>
            <Link to="/charters" className="text-sm font-medium text-slate-600 hover:text-cyan-500 transition-colors">
              Charters
            </Link>
            <Link to="/insights" className="text-sm font-medium text-slate-600 hover:text-cyan-500 transition-colors">
              Insights
            </Link>
            <Link to="/directory" className="text-sm font-medium text-slate-600 hover:text-cyan-500 transition-colors">
              Directory
            </Link>
            <Link to="/messages" className="text-sm font-medium text-slate-600 hover:text-cyan-500 transition-colors flex items-center gap-1 relative">
              <MessageSquare className="h-4 w-4" />
              Messages
              {unreadMessages > 0 && (
                <span className="absolute -top-1 -right-2 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                  {unreadMessages > 9 ? '9+' : unreadMessages}
                </span>
              )}
            </Link>
            <Link to="/news" className="text-sm font-medium text-slate-600 hover:text-cyan-500 transition-colors flex items-center gap-1">
              <Newspaper className="h-4 w-4" />
              News
            </Link>
            {!isAdmin && (
              <Link to="/advertising" className="text-sm font-medium text-slate-600 hover:text-cyan-500 transition-colors">
                Advertise
              </Link>
            )}
            {isAdmin && (
              <>
                <Link to="/admin/cms" className="text-sm font-medium text-orange-600 hover:text-orange-700 transition-colors">
                  CMS
                </Link>
                <Link to="/admin/applications" className="text-sm font-medium text-orange-600 hover:text-orange-700 transition-colors">
                  Applications
                </Link>
              </>
            )}
          </nav>

          {/* Desktop Buttons */}
          <div className="hidden md:flex items-center gap-2">
            <Button
              onClick={toggleDarkMode}
              variant="ghost"
              size="sm"
              className="p-2"
              data-testid="dark-mode-toggle"
            >
              {isDarkMode ? <Sun className="h-4 w-4 text-yellow-500" /> : <Moon className="h-4 w-4 text-slate-500" />}
            </Button>
            <Link to="/profile">
              <Button variant="outline" size="sm" data-testid="profile-button">
                <User className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Profile</span>
              </Button>
            </Link>
            <Button 
              onClick={handleLogout} 
              variant="outline" 
              size="sm"
              data-testid="logout-button"
            >
              <LogOut className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-slate-100 animate-in slide-in-from-top duration-200">
            <nav className="flex flex-col gap-1">
              <button
                onClick={() => handleNavClick(homeDestination)}
                className="flex items-center gap-3 px-3 py-3 text-base font-medium text-slate-700 hover:bg-slate-50 rounded-lg transition-colors"
              >
                {isAdmin ? <LayoutDashboard className="h-5 w-5 text-cyan-500" /> : <Home className="h-5 w-5 text-cyan-500" />}
                {isAdmin ? 'Admin Home' : 'Home'}
              </button>
              
              {!isAdmin && (
                <button
                  onClick={() => handleNavClick('/dashboard')}
                  className="flex items-center gap-3 px-3 py-3 text-base font-medium text-slate-700 hover:bg-slate-50 rounded-lg transition-colors"
                >
                  <Gauge className="h-5 w-5 text-cyan-500" />
                  Dashboard
                </button>
              )}
              
              <button
                onClick={() => handleNavClick('/rfqs')}
                className="flex items-center gap-3 px-3 py-3 text-base font-medium text-slate-700 hover:bg-slate-50 rounded-lg transition-colors"
              >
                <FileText className="h-5 w-5 text-cyan-500" />
                RFQ Marketplace
              </button>
              
              <button
                onClick={() => handleNavClick('/quotes')}
                className="flex items-center gap-3 px-3 py-3 text-base font-medium text-slate-700 hover:bg-slate-50 rounded-lg transition-colors"
              >
                <TrendingUp className="h-5 w-5 text-cyan-500" />
                Quotes
              </button>
              
              <button
                onClick={() => handleNavClick('/charters')}
                className="flex items-center gap-3 px-3 py-3 text-base font-medium text-slate-700 hover:bg-slate-50 rounded-lg transition-colors"
              >
                <Ship className="h-5 w-5 text-cyan-500" />
                Charters
              </button>
              
              <button
                onClick={() => handleNavClick('/messages')}
                className="flex items-center gap-3 px-3 py-3 text-base font-medium text-slate-700 hover:bg-slate-50 rounded-lg transition-colors relative"
              >
                <MessageSquare className="h-5 w-5 text-cyan-500" />
                Messages
                {unreadMessages > 0 && (
                  <span className="ml-auto bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    {unreadMessages > 9 ? '9+' : unreadMessages}
                  </span>
                )}
              </button>
              
              <button
                onClick={() => handleNavClick('/insights')}
                className="flex items-center gap-3 px-3 py-3 text-base font-medium text-slate-700 hover:bg-slate-50 rounded-lg transition-colors"
              >
                <BarChart3 className="h-5 w-5 text-cyan-500" />
                Freight Insights
              </button>
              
              <button
                onClick={() => handleNavClick('/directory')}
                className="flex items-center gap-3 px-3 py-3 text-base font-medium text-slate-700 hover:bg-slate-50 rounded-lg transition-colors"
              >
                <Users className="h-5 w-5 text-cyan-500" />
                Member Directory
              </button>
              
              <button
                onClick={() => handleNavClick('/news')}
                className="flex items-center gap-3 px-3 py-3 text-base font-medium text-slate-700 hover:bg-slate-50 rounded-lg transition-colors"
              >
                <Newspaper className="h-5 w-5 text-cyan-500" />
                News & Updates
              </button>
              
              {!isAdmin && (
                <button
                  onClick={() => handleNavClick('/advertising')}
                  className="flex items-center gap-3 px-3 py-3 text-base font-medium text-slate-700 hover:bg-slate-50 rounded-lg transition-colors"
                >
                  <Megaphone className="h-5 w-5 text-cyan-500" />
                  Advertise
                </button>
              )}
              
              {isAdmin && (
                <>
                  <div className="border-t border-slate-200 my-2"></div>
                  <p className="px-3 py-1 text-xs font-semibold text-orange-600 uppercase tracking-wider">Admin</p>
                  <button
                    onClick={() => handleNavClick('/admin/cms')}
                    className="flex items-center gap-3 px-3 py-3 text-base font-medium text-orange-700 hover:bg-orange-50 rounded-lg transition-colors"
                  >
                    <Newspaper className="h-5 w-5 text-orange-500" />
                    Content Management
                  </button>
                  <button
                    onClick={() => handleNavClick('/admin/applications')}
                    className="flex items-center gap-3 px-3 py-3 text-base font-medium text-orange-700 hover:bg-orange-50 rounded-lg transition-colors"
                  >
                    <ClipboardList className="h-5 w-5 text-orange-500" />
                    Applications
                  </button>
                </>
              )}
              
              <div className="border-t border-slate-200 my-2"></div>
              
              <button
                onClick={() => handleNavClick('/profile')}
                className="flex items-center gap-3 px-3 py-3 text-base font-medium text-slate-700 hover:bg-slate-50 rounded-lg transition-colors"
              >
                <User className="h-5 w-5 text-cyan-500" />
                Profile
              </button>
              
              <button
                onClick={handleLogout}
                className="flex items-center gap-3 px-3 py-3 text-base font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <LogOut className="h-5 w-5 text-red-500" />
                Logout
              </button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
